import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import type { ExperimentClient, Variant, Variants } from '@amplitude/experiment-js-client';

interface AmplitudeExperimentContextType {
    getVariant: (flagKey: string) => Variant | undefined;
    getVariantValue: (flagKey: string, fallback?: string) => string;
    getAllVariants: () => Variants;
    trackExposure: (flagKey: string) => void;
    refetchVariants: () => Promise<void>;
    isReady: boolean;
    isLoading: boolean;
}

const AmplitudeExperimentContext = createContext<AmplitudeExperimentContextType | undefined>(undefined);

interface AmplitudeExperimentProviderProps {
    children: ReactNode;
}

export const AmplitudeExperimentProvider: React.FC<AmplitudeExperimentProviderProps> = ({ children }) => {
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const clientRef = useRef<ExperimentClient | null>(null);
    const exposedFlags = useRef<Set<string>>(new Set());

    useEffect(() => {
        const deploymentKey = process.env.REACT_APP_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY;
        if (!deploymentKey) {
            console.warn('AmplitudeExperimentProvider: No deployment key found. Set REACT_APP_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY in .env');
            setIsLoading(false);
            return;
        }

        const loadExperiment = () => {
            import('@amplitude/experiment-js-client').then(({ Experiment }) => {
                const client = Experiment.initializeWithAmplitudeAnalytics(deploymentKey);
                clientRef.current = client;

                client.fetch()
                    .then(() => {
                        setIsReady(true);
                    })
                    .catch((err) => {
                        console.error('AmplitudeExperimentProvider: Failed to fetch variants', err);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            });
        };

        // Defer loading by 4s or first user interaction
        const timer = setTimeout(loadExperiment, 4000);
        const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
        const onInteraction = () => {
            clearTimeout(timer);
            setTimeout(loadExperiment, 50);
            events.forEach(e => document.removeEventListener(e, onInteraction));
        };
        events.forEach(e => document.addEventListener(e, onInteraction, { once: true, passive: true }));

        return () => {
            clearTimeout(timer);
            events.forEach(e => document.removeEventListener(e, onInteraction));
        };
    }, []);

    const getVariant = useCallback((flagKey: string): Variant | undefined => {
        return clientRef.current?.variant(flagKey);
    }, []);

    const getVariantValue = useCallback((flagKey: string, fallback: string = 'control'): string => {
        const variant = clientRef.current?.variant(flagKey);
        return variant?.value ?? fallback;
    }, []);

    const getAllVariants = useCallback((): Variants => {
        return clientRef.current?.all() ?? {};
    }, []);

    const trackExposure = useCallback((flagKey: string) => {
        if (!clientRef.current || exposedFlags.current.has(flagKey)) return;
        exposedFlags.current.add(flagKey);
        clientRef.current.exposure(flagKey);
    }, []);

    const refetchVariants = useCallback(async () => {
        if (!clientRef.current) return;
        setIsLoading(true);
        try {
            await clientRef.current.fetch();
            setIsReady(true);
        } catch (err) {
            console.error('AmplitudeExperimentProvider: Failed to refetch variants', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const value = useMemo<AmplitudeExperimentContextType>(() => ({
        getVariant,
        getVariantValue,
        getAllVariants,
        trackExposure,
        refetchVariants,
        isReady,
        isLoading,
    }), [getVariant, getVariantValue, getAllVariants, trackExposure, refetchVariants, isReady, isLoading]);

    return (
        <AmplitudeExperimentContext.Provider value={value}>
            {children}
        </AmplitudeExperimentContext.Provider>
    );
};

export const useAmplitudeExperiment = (): AmplitudeExperimentContextType => {
    const context = useContext(AmplitudeExperimentContext);
    if (!context) {
        throw new Error('useAmplitudeExperiment must be used within AmplitudeExperimentProvider');
    }
    return context;
};
